import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';

import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { ATTACHMENT_POLICY_RECEIPT } from '#/api/platform/attachments';
import { stockReceiptAttachmentsApi } from '#/api/imsMgmt/receipts/stockReceipt/attachments';
import { createStockReceipt } from '#/api/imsMgmt/receipts/stockReceipt/create';
import { fetchStockReceiptDetail } from '#/api/imsMgmt/receipts/stockReceipt/fetchDetail';
import { updateStockReceipt } from '#/api/imsMgmt/receipts/stockReceipt/update';
import { fetchCurrencySelectOptions } from '#/api/system/currency';
import {
  DocumentAttachmentsCard,
  uploadPendingAttachmentsAfterCreate,
} from '#/modules/commonComponents/attachments';
import { DocumentStatusBadge } from '#/modules/commonComponents/badge/DocumentStatusBadge';
import { Button } from '#/modules/commonComponents/button';
import FloatingDateInput from '#/modules/commonComponents/form/FloatingDateInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import StockReceiptHeaderChargeFields from
  '#/modules/imsMgmt/receipts/stockReceipt/components/StockReceiptHeaderChargeFields';
import FloatingSelect from '#/modules/commonComponents/form/select';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import PageShell from '#/modules/infra/layout/PageShell';
import useCreateEditSave from '#/hooks/useCreateEditSave';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import {
  canEditStockReceipt,
  canEditStockReceiptRemark,
} from '#/modules/imsMgmt/receipts/shared/receiptStatus';
import StockReceiptLinesEditor from '#/modules/imsMgmt/receipts/stockReceipt/components/StockReceiptLinesEditor';
import StockReceiptMainInfoCard from '#/modules/imsMgmt/receipts/stockReceipt/components/StockReceiptMainInfoCard';
import {
  getStockReceiptApiErrorMessage,
  isStockReceiptVersionConflict,
} from '#/modules/imsMgmt/receipts/stockReceipt/helper/apiError';
import {
  buildStockReceiptWritePayload,
  calcDocumentTotalAmount,
  headerChargePreviewAmount,
  mapStockReceiptDetailToFormValues,
} from '#/modules/imsMgmt/receipts/stockReceipt/helper/formSerialize';
import {
  resolveStockReceiptFieldLabel,
} from '#/modules/imsMgmt/receipts/stockReceipt/helper/formValidationLabels';
import { STOCK_RECEIPT_RECEIPT_DATE_TIP } from '#/modules/imsMgmt/receipts/stockReceipt/helper/receiptDateTip';
import { getStockReceiptSourceUiProfile } from '#/modules/imsMgmt/receipts/stockReceipt/helper/stockReceiptSource';
import {
  STOCK_RECEIPT_FIELD_LABELS,
  STOCK_RECEIPT_ROUNDING_MODE_OPTIONS,
  stockReceiptFormSchema,
} from '#/modules/imsMgmt/receipts/stockReceipt/schemas/stockReceiptSchema';
import { fetchSupplierSelectOptions } from '#/api/srm/supplier/fetchSelectOptions';
import { fetchWarehouseSelectOptions } from '#/modules/imsMgmt/settings/storePlace/helper/warehouseSelectOptions';
import { applyDocumentCurrencyFieldError } from '#/utils/documentCurrencyError';
import { formatDisplayAmount } from '#/utils/formatNumber';
import {
  lookupQueryKey,
  stockReceiptDetailQueryKey,
  stockReceiptListQueryKeyPrefix,
} from '#/utils/queryKeys';

const DEFAULT_VALUES = {
  receiptDate: '',
  expectedArrivalDate: '',
  warehouseId: '',
  supplierId: '',
  currencyId: '',
  roundingMode: '',
  customsDutyAmount: '',
  freightAmount: '',
  remark: '',
  issueReason: '',
  sourceType: 'MANUAL',
  sourceId: '',
  lines: [],
};

const CreateEditStockReceiptPage = () => {
  const { id } = useParams();
  const updatedAtRef = useRef(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
  } = useFormValidationSummary();

  const detailQuery = useQuery({
    queryKey: stockReceiptDetailQueryKey(id),
    queryFn: () => fetchStockReceiptDetail(id),
    enabled: Boolean(id),
  });

  const warehouseOptionsQuery = useQuery({
    queryKey: lookupQueryKey('warehouseSelect'),
    queryFn: fetchWarehouseSelectOptions,
  });

  const supplierOptionsQuery = useQuery({
    queryKey: lookupQueryKey('supplierSelect'),
    queryFn: fetchSupplierSelectOptions,
  });

  const currencyOptionsQuery = useQuery({
    queryKey: lookupQueryKey('currencySelect'),
    queryFn: fetchCurrencySelectOptions,
  });

  async function createStockReceiptWithAttachments(payload) {
    const result = await createStockReceipt(payload);
    const createdId = result?.data?.id;
    if (createdId) {
      await uploadPendingAttachmentsAfterCreate(
        stockReceiptAttachmentsApi,
        String(createdId),
        pendingFiles,
      );
    }
    return result;
  }

  const { isEdit, isPending, save } = useCreateEditSave({
    id,
    createFn: createStockReceiptWithAttachments,
    updateFn: updateStockReceipt,
    buildPayload: (values) => buildStockReceiptWritePayload(values, {
      updatedAt: id ? updatedAtRef.current : null,
    }),
    listQueryKeyPrefix: stockReceiptListQueryKeyPrefix,
    detailQueryKey: stockReceiptDetailQueryKey,
    getEditPath: ROUTES.stockReceiptEdit,
    getErrorMessage: (error) => getStockReceiptApiErrorMessage(error, '儲存失敗'),
    onError: (error) => applyDocumentCurrencyFieldError(error, setError),
    isVersionConflict: isStockReceiptVersionConflict,
    onVersionConflict: () => detailQuery.refetch(),
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(stockReceiptFormSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const watchedLines = useWatch({ control, name: 'lines', defaultValue: [] });
  const watchedCustomsDuty = useWatch({ control, name: 'customsDutyAmount' });
  const watchedFreight = useWatch({ control, name: 'freightAmount' });
  const amountPreviewTotal = useMemo(
    () => calcDocumentTotalAmount(watchedLines)
      + headerChargePreviewAmount(watchedCustomsDuty)
      + headerChargePreviewAmount(watchedFreight),
    [watchedLines, watchedCustomsDuty, watchedFreight],
  );

  const detailSourceType = detailQuery.data?.data?.sourceType ?? 'MANUAL';
  const sourceProfile = useMemo(
    () => getStockReceiptSourceUiProfile(isEdit ? detailSourceType : 'MANUAL'),
    [isEdit, detailSourceType],
  );
  const {
    showSupplierField,
    supplierEditable,
    showMoneySection,
    moneyEditable,
    showCommercialLinePricing,
    commercialLockTip,
  } = sourceProfile;

  useEffect(() => {
    const detail = detailQuery.data?.data;
    if (!detail) {
      return;
    }

    updatedAtRef.current = detail.updatedAt ?? detail.record?.updatedAt ?? null;
    resetValidationSummary();
    reset(mapStockReceiptDetailToFormValues(detail));
  }, [detailQuery.data, reset, resetValidationSummary]);

  const currencyOptions = useMemo(() => {
    const options = currencyOptionsQuery.data ?? [];
    const detail = detailQuery.data?.data;
    const summary = detail?.amountSummary;
    const currencyId = detail?.currencyId || summary?.currencyId;
    if (!currencyId) {
      return options;
    }
    if (options.some((item) => item.value === currencyId)) {
      return options;
    }
    const label = detail?.currencyCode || summary?.currencyCode || currencyId;
    return [...options, { value: currencyId, label }];
  }, [currencyOptionsQuery.data, detailQuery.data?.data]);

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSubmit(values) {
    markSubmitAttempted();
    save(values);
  }

  const detail = detailQuery.data?.data;
  const pageTitle = isEdit ? '編輯入庫單' : '新增入庫單';
  const headerStatus = isEdit ? (detail?.status || 'DRAFT') : 'DRAFT';
  const isPageLoading = isEdit && detailQuery.isLoading;
  const isDetailNotFound = isEdit && detailQuery.isError;
  const warehouseOptions = warehouseOptionsQuery.data ?? [];
  const supplierOptions = supplierOptionsQuery.data ?? [];

  if (isEdit && detail && !canEditStockReceipt(detail.status)) {
    if (canEditStockReceiptRemark(detail.status)) {
      return <Navigate to={ROUTES.stockReceiptRemark(id)} replace />;
    }
    return <Navigate to={ROUTES.stockReceiptDetail(id)} replace />;
  }

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="w-full">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0! flex flex-wrap items-center gap-2">
              <span className="border-l-5 border-primary-400 pl-2.5">{pageTitle}</span>
              <DocumentStatusBadge status={headerStatus} size="sm" />
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={id ? ROUTES.stockReceiptDetail(id) : ROUTES.stockReceipt}>
                {id ? '返回詳情' : '返回列表'}
              </Link>
            </Button>
          </div>

          {isPageLoading ? (
            <p className="text-sm text-slate-500">載入中…</p>
          ) : null}

          {isDetailNotFound ? (
            <p className="text-sm text-destructive">
              {getStockReceiptApiErrorMessage(detailQuery.error, '找不到入庫單')}
            </p>
          ) : null}

          {!isPageLoading && !isDetailNotFound ? (
            <div className="space-y-4">
              {isEdit && detail ? (
                <div>
                  <p className="mb-2 text-sm font-medium text-gray-600">原始單據資訊</p>
                  <StockReceiptMainInfoCard detail={detail} />
                </div>
              ) : null}

              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={STOCK_RECEIPT_FIELD_LABELS}
                fieldLabelResolver={(path) => resolveStockReceiptFieldLabel(path, {
                  lines: getValues('lines'),
                })}
                customMessages={customMessages}
              />

              <form
                className="sheet create_edit create_edit_form create_edit_form--page-scroll"
                onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
                noValidate
              >
                <div className="create_edit_form__body divide-y divide-gray-500 pb-0!">
                  <section className="pb-6">
                    <div
                      className={cn(
                        'grid w-full grid-cols-1 divide-y divide-gray-200',
                        '@4xl/sheet:grid-cols-2',
                        '@4xl/sheet:divide-x @4xl/sheet:divide-y-0',
                      )}
                    >
                      <div
                        className={cn(
                          '@container/sr-doc min-w-0 space-y-3 pb-6',
                          '@4xl/sheet:pr-6 @4xl/sheet:pb-0',
                        )}
                      >
                        <div className="form-block">
                          <h2 className="form-block__title">單據資訊</h2>
                        </div>
                        <div
                          className={cn(
                            'grid grid-cols-1 gap-3',
                            '@sm/sr-doc:grid-cols-2',
                          )}
                        >
                          <FloatingDateInput
                            id="receiptDate"
                            label="製造商開單日"
                            tip={STOCK_RECEIPT_RECEIPT_DATE_TIP}
                            control={control}
                            errors={errors}
                            placeholder="西元年/月/日"
                          />
                          {showSupplierField ? (
                            <FloatingSelect
                              id="supplierId"
                              label="供應商"
                              tip={commercialLockTip}
                              options={supplierOptions}
                              control={control}
                              errors={errors}
                              isClearable={supplierEditable}
                              isDisabled={
                                !supplierEditable
                                || supplierOptionsQuery.isLoading
                              }
                              noOptionsMessage="無可用供應商"
                            />
                          ) : null}
                          <FloatingSelect
                            id="warehouseId"
                            label="倉別"
                            options={warehouseOptions}
                            control={control}
                            errors={errors}
                            isDisabled={warehouseOptionsQuery.isLoading}
                            noOptionsMessage="無可用倉別"
                          />
                          {showSupplierField ? (
                            <div
                              className="hidden @sm/sr-doc:block"
                              aria-hidden="true"
                            />
                          ) : null}
                          <FloatingTextarea
                            id="issueReason"
                            label="事由"
                            containerClassName={cn(
                              'col-span-full',
                              '@2xl/sr-doc:col-span-1',
                            )}
                            register={register}
                            errors={errors}
                            rows={2}
                          />
                          <FloatingTextarea
                            id="remark"
                            label="備註"
                            containerClassName={cn(
                              'col-span-full',
                              '@2xl/sr-doc:col-span-1',
                            )}
                            register={register}
                            errors={errors}
                            rows={2}
                          />
                        </div>
                      </div>

                      <div
                        className={cn(
                          '@container/sr-money min-w-0 space-y-3 pt-6',
                          '@4xl/sheet:pl-6 @4xl/sheet:pt-0',
                          !showMoneySection && 'hidden',
                        )}
                      >
                        <div className="form-block">
                          <h2 className="form-block__title">金額</h2>
                        </div>
                        <div
                          className={cn(
                            'grid grid-cols-1 gap-3',
                            '@sm/sr-money:grid-cols-2',
                          )}
                        >
                          <FloatingSelect
                            id="currencyId"
                            label="幣別"
                            tip={commercialLockTip}
                            options={currencyOptions}
                            control={control}
                            errors={errors}
                            isDisabled={
                              !moneyEditable
                              || currencyOptionsQuery.isLoading
                            }
                            noOptionsMessage="無可用幣別"
                          />
                          <FloatingSelect
                            id="roundingMode"
                            label="進位法"
                            tip={commercialLockTip}
                            options={STOCK_RECEIPT_ROUNDING_MODE_OPTIONS}
                            control={control}
                            errors={errors}
                            allowEmpty
                            isClearable={false}
                            isDisabled={!moneyEditable}
                          />
                          <StockReceiptHeaderChargeFields
                            register={register}
                            errors={errors}
                          />
                          <div
                            className={cn(
                              'col-span-full min-w-0 rounded-md',
                              'border border-gray-200 bg-gray-50 px-4 py-3',
                            )}
                          >
                            <p className="text-xs text-gray-500">總金額（預覽）</p>
                            <p className="mt-1 text-xl font-bold tabular-nums text-gray-900 underline">
                              {formatDisplayAmount(amountPreviewTotal)}
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              預覽含關稅與運費。實際總額由後端計算，不計入稅額，也不改明細單價。
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  <StockReceiptLinesEditor
                    control={control}
                    register={register}
                    errors={errors}
                    setValue={setValue}
                    showCommercialLinePricing={showCommercialLinePricing}
                  />

                  {isEdit && id ? (
                    <DocumentAttachmentsCard
                      variant="section"
                      className="pb-6"
                      businessId={String(id)}
                      ownerKey="stockReceipt"
                      api={stockReceiptAttachmentsApi}
                      policy={ATTACHMENT_POLICY_RECEIPT}
                    />
                  ) : !isEdit ? (
                    <DocumentAttachmentsCard
                      variant="section"
                      className="pb-6"
                      ownerKey="stockReceipt"
                      api={stockReceiptAttachmentsApi}
                      policy={ATTACHMENT_POLICY_RECEIPT}
                      pendingFiles={pendingFiles}
                      onPendingFilesChange={setPendingFiles}
                    />
                  ) : null}
                </div>

                <div className="create_edit_form__actions mt-4 flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="outline" asChild>
                    <Link to={id ? ROUTES.stockReceiptDetail(id) : ROUTES.stockReceipt}>
                      取消
                    </Link>
                  </Button>
                  <Button type="submit" disabled={isPending}>
                    {isPending ? '儲存中…' : '儲存'}
                  </Button>
                </div>
              </form>
            </div>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditStockReceiptPage;
